import { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../services/authService';

/**
 * Load published presentation overrides for a page/form.
 * On any failure or missing publish → fallback:true (use hard-coded UI).
 */
export const useFormConfiguration = ({
  pageKey,
  formKey,
  mode = 'create',
  enabled = true,
  /** Map UI state keys → inventory fieldKeys when they differ (Phase 7). */
  fieldKeyAliases = {},
} = {}) => {
  const [state, setState] = useState({
    loading: Boolean(enabled && pageKey && formKey),
    error: null,
    fallback: true,
    config: null,
  });

  useEffect(() => {
    if (!enabled || !pageKey || !formKey) {
      setState({ loading: false, error: null, fallback: true, config: null });
      return undefined;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    adminAPI.getRuntimeFormConfig(pageKey, formKey, { mode })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data || res?.data || { fallback: true };
        setState({
          loading: false,
          error: null,
          fallback: Boolean(data.fallback),
          config: data,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          loading: false,
          error: err?.message || 'config_load_failed',
          fallback: true,
          config: null,
        });
      });
    return () => { cancelled = true; };
  }, [pageKey, formKey, mode, enabled]);

  const fieldMap = useMemo(() => state.config?.fieldMap || {}, [state.config]);
  const fields = useMemo(() => state.config?.fields || [], [state.config]);

  const customFields = useMemo(() => {
    if (state.fallback) return [];
    if (!state.config?.customFieldsEnabled) return [];
    const fromApi = state.config?.customFields;
    if (Array.isArray(fromApi)) {
      return fromApi.filter((f) => f && !f.hidden);
    }
    return fields.filter((f) => f.fieldClass === 'CUSTOM' && !f.hidden);
  }, [state.fallback, state.config, fields]);

  const resolveKey = (fieldKey) => {
    if (fieldMap[fieldKey]) return fieldKey;
    const alias = fieldKeyAliases?.[fieldKey];
    if (alias && fieldMap[alias]) return alias;
    // reverse: inventory key passed when UI uses alias
    for (const [uiKey, invKey] of Object.entries(fieldKeyAliases || {})) {
      if (invKey === fieldKey && fieldMap[uiKey]) return uiKey;
    }
    return fieldKey;
  };

  const getField = (fieldKey) => fieldMap[resolveKey(fieldKey)] || fieldMap[fieldKey] || null;

  const permissionsFor = (fieldKey) => {
    const f = getField(fieldKey);
    if (!f || state.fallback || !state.config?.fieldPermissionsEnabled) {
      return { canView: true, canCreate: true, canEdit: true, canClear: true, canExport: true };
    }
    return f.permissions || { canView: true, canCreate: true, canEdit: true, canClear: false, canExport: true };
  };

  const labelFor = (fieldKey, defaultLabel) => {
    const f = getField(fieldKey);
    if (!f || state.fallback) return defaultLabel;
    return f.label || defaultLabel;
  };

  const placeholderFor = (fieldKey, defaultPlaceholder = '') => {
    const f = getField(fieldKey);
    if (!f || state.fallback) return defaultPlaceholder;
    return f.placeholder != null && f.placeholder !== '' ? f.placeholder : defaultPlaceholder;
  };

  const helpFor = (fieldKey, defaultHelp = '') => {
    const f = getField(fieldKey);
    if (!f || state.fallback) return defaultHelp;
    return f.helpText || defaultHelp;
  };

  const isHidden = (fieldKey) => {
    if (state.fallback) return false;
    return Boolean(getField(fieldKey)?.hidden);
  };

  const isRequired = (fieldKey, defaultRequired = false) => {
    if (state.fallback) return defaultRequired;
    const f = getField(fieldKey);
    if (!f) return defaultRequired;
    return Boolean(f.required);
  };

  const isReadOnly = (fieldKey, defaultReadOnly = false) => {
    if (state.fallback) return defaultReadOnly;
    const f = getField(fieldKey);
    if (!f) return defaultReadOnly;
    return Boolean(f.readOnly);
  };

  return {
    ...state,
    fields,
    fieldMap,
    customFields,
    customFieldsEnabled: Boolean(state.config?.customFieldsEnabled) && !state.fallback,
    fieldPermissionsEnabled: Boolean(state.config?.fieldPermissionsEnabled) && !state.fallback,
    rulesEnabled: Boolean(state.config?.rulesEnabled) && !state.fallback,
    rules: state.fallback ? [] : (state.config?.rules || []),
    getField,
    permissionsFor,
    labelFor,
    placeholderFor,
    helpFor,
    isHidden,
    isRequired,
    isReadOnly,
  };
};

export default useFormConfiguration;
